import {
  applySnapshot,
  flow,
  getRoot,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { DeleteAllowedDomainRPC } from "@pulse/pulse-rpcs/lib/email-cycle/deleteAllowedDomainRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useDeleteAllowedDomainRPCClientImpl } from "../rpcs/RPC";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  BoundedNameModel,
  createBoundedNameModel,
} from "../../models/BoundedNameModel";
import { AllowedDomainsStore } from "./AllowedDomainsStore";

const getInitialState = (): SnapshotIn<typeof DeleteAllowedDomainStore> => ({
  domainId: EMPTY_CHARACTER,
  domainName: createBoundedNameModel(),
  loadingDeleteAllowedDomain: false,
  deleteAllowedDomainRPCError: null,
});

export const DeleteAllowedDomainStore = types
  .model("DeleteAllowedDomainStore", {
    domainId: types.string,
    domainName: BoundedNameModel,
    loadingDeleteAllowedDomain: types.boolean,
    deleteAllowedDomainRPCError: types.maybeNull(
      types.enumeration(
        "DeleteAllowedDomainRPCError",
        Object.values(DeleteAllowedDomainRPC.RPCError),
      ),
    ),
  })
  .actions((store) => ({
    setAllowedDomain: (domainId: string, domainName: string): void => {
      store.domainId = domainId;
      store.domainName.setName(domainName);
    },
    deleteAllowedDomain: flow(function* () {
      try {
        store.loadingDeleteAllowedDomain = true;
        store.deleteAllowedDomainRPCError = null;

        const apiClient = getAPIClient(store);
        const request = new DeleteAllowedDomainRPC.Request(
          new LeoUUID(store.domainId),
        );

        const result: LeoRPCResult<
          DeleteAllowedDomainRPC.Response,
          DeleteAllowedDomainRPC.Errors.Errors
        > =
          yield useDeleteAllowedDomainRPCClientImpl(apiClient).execute(request);

        if ("response" in result) {
          const allowedDomainsStore = (
            getRoot(store) as {
              allowedDomainsStore: Instance<typeof AllowedDomainsStore>;
            }
          ).allowedDomainsStore;
          yield allowedDomainsStore.refreshCurrentPageAfterDeletion();
        } else {
          switch (result.error.code) {
            case DeleteAllowedDomainRPC.RPCError.DomainNotFound:
              store.deleteAllowedDomainRPCError = result.error.code;
              break;
            default:
              result.error.code satisfies never;
          }
        }
      } finally {
        store.loadingDeleteAllowedDomain = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createDeleteAllowedDomainStore = (): Instance<
  typeof DeleteAllowedDomainStore
> => DeleteAllowedDomainStore.create(getInitialState());
